/**
 * Depot Manager dashboard — Tier 2 (spec §6.1). Weekly view over the daily
 * returns of the depot: send back with reason, accept FLAGGED with
 * justification, and sign the weekly attestation with the manager's device
 * key. Styled strictly per DESIGN.md; RECONCILED/SIGNED carry the sole
 * semantic green (decision D-UI1).
 */
import { useEffect, useMemo, useState } from "react";
import { generateKeyPair, signArtefact, type KeyPair } from "@vra/signing";
import { addDays, type WeeklyAttestation } from "@vra/schema";
import { InfoTip } from "./info-tip";
import { Tour, type TourStep } from "./tour";

const DEPOT_ID = "DL-DEP-001";

const SETUP_TOUR: TourStep[] = [
  {
    selector: '[data-tour="manager-setup"]',
    title: "One-time device setup",
    body: "Register this device under your manager officer ID before you can send back, accept, or sign anything. It's bound to this device's signing key, same as the depot capture app.",
  },
];

const MAIN_TOUR: TourStep[] = [
  { selector: ".top-nav", title: "Welcome to the Depot Dashboard", body: "This is Tier 2 — the manager's weekly view over every daily return filed by the depot: reconciling variances, sending back or accepting flagged days, and signing the week off." },
  { selector: '[data-tour="week-card"]', title: "A week at a glance", body: "Every bus-day filed that week, one row each. RECONCILED and SIGNED are the only statuses shown in green — everything else needs your attention." },
  { selector: '[data-tour="table-header"]', title: "Reading the columns", body: "Op km is what the depot filed (Channel A); ITMS km is the bus's own telemetry (Channel B); Variance is the gap between them. Look for the \"i\" icons on column headers if a label is unclear." },
  { selector: '[data-tour="row-actions"]', title: "Row actions", body: "UNRECONCILED days can have telemetry simulated (demo only) or be sent back. FLAGGED days — variance beyond tolerance — can be accepted with a written justification or sent back to the depot to re-file." },
  { selector: '[data-tour="sign-week"]', title: "Sign the week", body: "Once every bus-day in the week is RECONCILED or ACCEPTED, sign the weekly attestation with your device key. This locks the week's records." },
];

interface ServerDaily {
  key: string;
  version: number;
  status: string;
  varianceBps?: number;
  itmsKm?: number;
  sendBackReason?: string;
  acceptJustification?: string;
  weeklyKey?: string;
  signed: { digestSha256Hex: string; artefact: { date: string; busRegistrationNo: string; actualKm: number } };
}

interface ServerWeekly {
  key: string;
  status: string;
  officerRef: string;
  ledgerTimestamp: string;
}

const KEYS_STORAGE = "vra-manager-keys";
const REF_STORAGE = "vra-manager-ref";

function loadKeys(): KeyPair {
  const raw = localStorage.getItem(KEYS_STORAGE);
  if (raw) return JSON.parse(raw);
  const fresh = generateKeyPair();
  localStorage.setItem(KEYS_STORAGE, JSON.stringify(fresh));
  return fresh;
}

/** Monday of the ISO week containing the date. */
function weekStartOf(date: string): string {
  const d = new Date(date + "T00:00:00Z");
  const dow = (d.getUTCDay() + 6) % 7; // Mon=0
  return addDays(date, -dow);
}

export function App() {
  const [managerRef, setManagerRef] = useState(localStorage.getItem(REF_STORAGE) ?? "");
  const [saved, setSaved] = useState(!!localStorage.getItem(REF_STORAGE));
  const [keys] = useState(loadKeys);
  const [daily, setDaily] = useState<ServerDaily[]>([]);
  const [weekly, setWeekly] = useState<ServerWeekly[]>([]);
  const [msg, setMsg] = useState("");
  const [tourActive, setTourActive] = useState(false);

  async function refresh() {
    const [d, w] = await Promise.all([
      fetch("/api/v1/daily-returns").then((r) => r.json()),
      fetch("/api/v1/weekly-attestations").then((r) => r.json()),
    ]);
    setDaily(d);
    setWeekly(w);
  }

  useEffect(() => {
    refresh().catch(() => setMsg("Backend unreachable — is the submission API running?"));
  }, []);

  const weeks = useMemo(() => {
    const byWeek = new Map<string, ServerDaily[]>();
    for (const rec of daily) {
      if (rec.status === "SUPERSEDED") continue;
      const ws = weekStartOf(rec.signed.artefact.date);
      byWeek.set(ws, [...(byWeek.get(ws) ?? []), rec]);
    }
    return [...byWeek.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [daily]);

  async function act(path: string, body: unknown) {
    const res = await fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    const j = await res.json();
    setMsg(res.ok ? "Done." : `Rejected: ${j.error}`);
    await refresh();
  }

  function sendBack(rec: ServerDaily) {
    const reason = window.prompt("Recorded reason for send-back (spec 6.4-1):");
    if (!reason) return;
    const a = rec.signed.artefact;
    act("/api/v1/daily-returns/send-back", {
      depotId: DEPOT_ID, date: a.date, busRegistrationNo: a.busRegistrationNo,
      reason, managerRef,
    });
  }

  /** Demo convenience: play the ITMS vendor for an UNRECONCILED bus-day
   *  right from this screen (endpoint exists only on the dev/demo server). */
  function simulateTelemetry(rec: ServerDaily) {
    const a = rec.signed.artefact;
    const kmStr = window.prompt(
      `ITMS kilometres for ${a.busRegistrationNo} on ${a.date}?\n` +
      `(filed: ${a.actualKm} km — within 5% reconciles, beyond 5% flags)`,
      String(a.actualKm),
    );
    if (!kmStr) return;
    act("/api/v1/demo/simulate-itms", {
      date: a.date,
      busRegistrationNo: a.busRegistrationNo,
      itmsKm: parseInt(kmStr, 10),
    });
  }

  function accept(rec: ServerDaily) {
    const justification = window.prompt("Recorded justification for accepting FLAGGED (spec 5.2):");
    if (!justification) return;
    const a = rec.signed.artefact;
    act("/api/v1/daily-returns/accept-flagged", {
      depotId: DEPOT_ID, date: a.date, busRegistrationNo: a.busRegistrationNo,
      justification, managerRef,
    });
  }

  async function signWeek(weekStart: string, recs: ServerDaily[]) {
    const attestable = recs.filter(
      (r) => r.status === "RECONCILED" || r.status === "ACCEPTED_WITH_JUSTIFICATION",
    );
    if (attestable.length !== recs.length) {
      const unrec = recs.filter((r) => r.status === "UNRECONCILED").length;
      setMsg(
        unrec > 0
          ? `Week not attestable: ${unrec} bus-day(s) still awaiting ITMS telemetry (Channel B) — use "Simulate telemetry…" on those rows, or resolve FLAGGED ones (spec 6.4).`
          : "Week not attestable: every daily must be RECONCILED or ACCEPTED (spec 6.4).",
      );
      return;
    }
    const artefact: WeeklyAttestation = {
      artefactType: "WEEKLY_ATTESTATION",
      version: 1,
      depotId: DEPOT_ID,
      weekStart,
      dailyRefs: attestable.map((r) => ({
        date: r.signed.artefact.date,
        busRegistrationNo: r.signed.artefact.busRegistrationNo,
        digestSha256Hex: r.signed.digestSha256Hex,
        version: r.version,
      })),
    };
    const signed = await signArtefact(artefact as never, keys, managerRef);
    const res = await fetch("/api/v1/weekly-attestations", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(signed),
    });
    const j = await res.json();
    setMsg(res.ok ? `Weekly attestation signed: ${j.key}` : `Rejected: ${j.error}`);
    await refresh();
  }

  if (!saved) {
    return (
      <div className="app">
        <nav className="top-nav">
          <span className="brand"><span className="mark">●</span> VRA · Depot Dashboard</span>
          <button className="btn-secondary" onClick={() => setTourActive(true)}>Take a tour</button>
        </nav>
        <Tour steps={SETUP_TOUR} active={tourActive} onClose={() => setTourActive(false)} />
        <section className="card" data-tour="manager-setup">
          <h2>Manager setup</h2>
          <p className="eyebrow">Tier 2 — Depot Manager. Register this device key under your officer ID (§6.3).</p>
          <div className="grid">
            <div>
              <label>Manager officer ID
                <InfoTip text="Your manager officer identifier, e.g. MGR-07 — not your name. This device's signing key gets bound to it, so every send-back, acceptance, and weekly signature is traceable to you without a name on the ledger." />
              </label>
              <input value={managerRef} onChange={(e) => setManagerRef(e.target.value)} placeholder="MGR-…" />
            </div>
          </div>
          <p><label>Device public key (share with the registrar)
              <InfoTip text="This device's cryptographic public key. Share it with the registrar so it can be bound to your manager officer ID — this is what makes your signed acceptances and weekly attestations verifiable." />
            </label>
            <span className="mono">{keys.publicKeyHex}</span></p>
          <button className="btn-primary" onClick={async () => {
            if (!managerRef.trim()) return;
            // Dev convenience: self-register (production: registrar + ePramaan, §6.3)
            try {
              const res = await fetch("/api/v1/officers", {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({
                  officerId: managerRef.trim(),
                  role: "DEPOT_MANAGER",
                  publicKeyHex: keys.publicKeyHex,
                }),
              });
              if (!res.ok && res.status !== 409) {
                setMsg(`Registration failed: ${(await res.json()).error}`);
                return;
              }
            } catch {
              setMsg("Backend unreachable — start the submission API first.");
              return;
            }
            localStorage.setItem(REF_STORAGE, managerRef.trim());
            setSaved(true);
          }}>Save</button>
        </section>
      </div>
    );
  }

  return (
    <div className="app">
      <nav className="top-nav">
        <span className="brand"><span className="mark">●</span> VRA · Depot Dashboard</span>
        <span className="actions">
          <button className="btn-secondary" onClick={() => setTourActive(true)}>Take a tour</button>
          <span className="badge">{DEPOT_ID} · <span className="mono">{managerRef}</span></span>
        </span>
      </nav>
      <Tour steps={MAIN_TOUR} active={tourActive} onClose={() => setTourActive(false)} />

      {msg && <p className="eyebrow">{msg}</p>}

      {weeks.length === 0 && <section className="card"><p>No daily returns filed yet.</p></section>}

      {weeks.map(([weekStart, recs], weekIdx) => {
        const attested = weekly.find((w) => w.key === `weekly/${DEPOT_ID}/${weekStart}`);
        return (
          <section className="card" key={weekStart} data-tour={weekIdx === 0 ? "week-card" : undefined}>
            <h2>Week of {weekStart}
              {attested && <span className="badge success" style={{ marginLeft: 12 }}>SIGNED</span>}
            </h2>
            <table>
              <thead>
                <tr data-tour={weekIdx === 0 ? "table-header" : undefined}>
                  <th>Date</th><th>Bus</th>
                  <th>v<InfoTip text="Version — how many times this daily return has been re-filed after a send-back. Version 1 is the original filing." /></th>
                  <th>Op km<InfoTip text="Actual kilometres as filed by the depot — Channel A, the operator's own record." /></th>
                  <th>ITMS km<InfoTip text="Kilometres reported by the bus's own telemetry system — Channel B, an independent source." /></th>
                  <th>Variance<InfoTip text="The gap between Op km and ITMS km, in basis points (1 bps = 0.01%). Beyond the tolerance threshold, the bus-day is FLAGGED for review." /></th>
                  <th>Status<InfoTip text="UNRECONCILED (awaiting telemetry) → RECONCILED or FLAGGED → optionally ACCEPTED_WITH_JUSTIFICATION or SENT_BACK. Only RECONCILED and SIGNED show in green." /></th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recs.sort((a, b) => (a.signed.artefact.date < b.signed.artefact.date ? -1 : 1)).map((r, rowIdx) => {
                  const a = r.signed.artefact;
                  const rowActionsTour = weekIdx === 0 && rowIdx === 0 ? "row-actions" : undefined;
                  return (
                    <tr key={r.key + r.version}>
                      <td className="mono">{a.date}</td>
                      <td className="mono">{a.busRegistrationNo}</td>
                      <td className="mono">{r.version}</td>
                      <td className="mono">{a.actualKm}</td>
                      <td className="mono">{r.itmsKm ?? "—"}</td>
                      <td className="mono">{r.varianceBps !== undefined ? `${r.varianceBps} bps` : "—"}</td>
                      <td>
                        <span className={`badge ${r.status === "RECONCILED" ? "success" : ""}`}
                          title={r.sendBackReason ?? r.acceptJustification ?? ""}>
                          {r.status}
                        </span>
                      </td>
                      <td data-tour={rowActionsTour}>
                        {!r.weeklyKey && r.status === "FLAGGED" && (
                          <span className="actions">
                            <button className="btn-secondary" onClick={() => accept(r)}>Accept…</button>
                            <button className="btn-secondary" onClick={() => sendBack(r)}>Send back…</button>
                          </span>
                        )}
                        {!r.weeklyKey && r.status === "UNRECONCILED" && (
                          <span className="actions">
                            <button className="btn-primary" onClick={() => simulateTelemetry(r)}>
                              Simulate telemetry…
                            </button>
                            <button className="btn-secondary" onClick={() => sendBack(r)}>Send back…</button>
                          </span>
                        )}
                        {!r.weeklyKey && (r.status === "RECONCILED" || r.status === "ACCEPTED_WITH_JUSTIFICATION") && (
                          <button className="btn-secondary" onClick={() => sendBack(r)}>Send back…</button>
                        )}
                        {r.weeklyKey && <span className="eyebrow">attested</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!attested && (
              <div className="actions" style={{ marginTop: 16 }} data-tour="sign-week">
                <button className="btn-primary" onClick={() => signWeek(weekStart, recs)}>
                  Sign weekly attestation
                </button>
                <span className="eyebrow" style={{ alignSelf: "center" }}>
                  requires every bus-day RECONCILED or ACCEPTED (§6.4)
                </span>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
